import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
  Autocomplete,
  Typography,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { printPurchasePaymentThermal } from "../utils/printPurchasePaymentThermal";

const PurchasePaymentAdd = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd(
    "shop/purchase-payments",
    token
  );

  // Fetch purchase details and currencies for selection
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies",
    token
  );
  const { data: purchasesData, loading: purchaseLoading } = useFetchObjects(
    ["purchase"],
    "shop/purchases",
    token
  );

  const currencies = Array.isArray(currenciesData?.results) ? currenciesData.results : (Array.isArray(currenciesData) ? currenciesData : []);
  const purchases = Array.isArray(purchasesData?.results) ? purchasesData.results : (Array.isArray(purchasesData) ? purchasesData : []);

  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [currencyDetails, setCurrencyDetails] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [remainedAmount, setRemainedAmount] = useState(0);
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 16)
  ); // Format for datetime-local
  const [notes, setNotes] = useState("");

  // Handle purchase selection
  const handlePurchaseChange = (event, value) => {
    setSelectedPurchase(value);
    if (value) {
      const currency = value.currency_details;
      setCurrencyDetails(currency);
      setSelectedCurrency(currency.id);
      setTotalAmount(value.total_price[currency.title] || 0);
      setPaidAmount(value.total_paid[currency.title] || 0);
      setRemainedAmount(value.total_remained_amount[currency.title] || 0);
    } else {
      setCurrencyDetails(null);
      setSelectedCurrency("");
      setTotalAmount(0);
      setPaidAmount(0);
      setRemainedAmount(0);
    }
  };

  // Handle amount validation
  const handleAmountChange = (e) => {
    const value = parseFloat(e.target.value);
    if (value <= remainedAmount) {
      setAmount(value);
    } else {
      console.error("Amount cannot exceed the remaining amount.");
    }
  };

  const handleSubmit = () => {
    if (!selectedPurchase || !amount || amount > remainedAmount) {
      console.error("Validation failed.");
      return;
    }

    const formData = new FormData();
    formData.append("purchase", selectedPurchase.id);
    formData.append("currency", selectedCurrency);
    formData.append("amount", amount);
    formData.append("payment_date", paymentDate); // Submit payment date
    formData.append("notes", notes);
    handleAdd(formData);
  };

  // Reset form after successful addition
  useEffect(() => {
    if (resetForm) {
      setSelectedPurchase(null);
      setSelectedCurrency("");
      setAmount("");
      setCurrencyDetails(null);
      setTotalAmount(0);
      setPaidAmount(0);
      setRemainedAmount(0);
      setPaymentDate(new Date().toISOString().slice(0, 16)); // Reset payment date
      setNotes("");
      onClose();
    }
  }, [resetForm, onClose]);

  // Safeguard against undefined data
  const purchaseList = purchases || [];
  const currencyList = currencies || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("purchasePayment.addPayment")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {/* Purchase Selection with Autocomplete */}
          <Autocomplete
            options={purchaseList}
            getOptionLabel={(option) =>
              `Purchase #${option.id} - ${option.supplier_details?.name || 'N/A'} - ${option.shop_details?.name || 'N/A'}`
            }
            value={selectedPurchase}
            onChange={handlePurchaseChange}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label={t("purchasePayment.selectPurchase")}
                fullWidth
                required
              />
            )}
            loading={purchaseLoading}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Typography>
                  <strong>Purchase #{option.id}</strong> - {option.supplier_details?.name || 'N/A'} - {option.shop_details?.name || 'N/A'}
                </Typography>
              </Box>
            )}
          />

          {/* Display Total, Paid, and Remaining Amounts */}
          {selectedPurchase && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Total:</strong> {totalAmount.toFixed(2)} {currencyDetails?.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Paid:</strong> {paidAmount.toFixed(2)} {currencyDetails?.title}
              </Typography>
              <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                <strong>Remaining:</strong> {remainedAmount.toFixed(2)} {currencyDetails?.title}
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                sx={{ mt: 1 }}
                onClick={() => printPurchasePaymentThermal({
                  totalAmount: totalAmount,
                  requestedAmount: amount || 0,
                  remainingAmount: remainedAmount,
                  currencySymbol: currencyDetails?.title,
                  supplierName: selectedPurchase?.supplier_details?.name
                })}
                disabled={!amount}
              >
                Print Receipt
              </Button>
            </Box>
          )}

          {/* Currency Selection (Disabled, as it's derived from the purchase) */}
          <TextField
            select
            margin="dense"
            label={t("purchasePayment.selectCurrency")}
            fullWidth
            required
            value={selectedCurrency}
            disabled
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : currencyList.length > 0 ? (
              <MenuItem value={selectedCurrency}>
                {currencyDetails?.title}
              </MenuItem>
            ) : (
              <MenuItem value="" disabled>
                {t("purchasePayment.noCurrenciesAvailable")}
              </MenuItem>
            )}
          </TextField>

          {/* Amount Input */}
          <TextField
            margin="dense"
            label={t("purchasePayment.amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={handleAmountChange}
            inputProps={{
              max: remainedAmount,
            }}
            error={amount > remainedAmount}
            helperText={
              amount > remainedAmount
                ? "Amount cannot exceed the remaining amount."
                : ""
            }
          />

          {/* Payment Date Input */}
          <TextField
            margin="dense"
            label={t("purchasePayment.paymentDate")}
            type="datetime-local" // Using datetime-local input
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)} // Handle date change
          />

          <TextField
            margin="dense"
            label={t("purchasePayment.notes")}
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("purchasePayment.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading || !selectedPurchase || !selectedCurrency || !amount
          }
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("purchasePayment.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchasePaymentAdd;
